"use client";

import { useState, useRef, useEffect } from "react";
import { ChevronDown, Search } from "lucide-react";
import { countries, Country } from "@/lib/countries";

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function PhoneInput({ value, onChange, placeholder, className = "", disabled }: PhoneInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Try to parse the current value to find the country
  const [selectedCountry, setSelectedCountry] = useState<Country>(() => {
    const matched = countries.find(c => value.startsWith(c.dialCode));
    return matched || countries.find(c => c.code === "AU") || countries[0];
  });

  const displayValue = value.startsWith(selectedCountry.dialCode) 
    ? value.slice(selectedCountry.dialCode.length) 
    : value;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredCountries = countries.filter(c => 
    c.name.toLowerCase().includes(search.toLowerCase()) || 
    c.dialCode.includes(search)
  );

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    // If the value already has a dial code, replace it
    const cleanValue = value.replace(/^\+\d+/, "");
    onChange(country.dialCode + cleanValue);
    setIsOpen(false);
    setSearch("");
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const num = e.target.value.replace(/\D/g, ""); // Only digits
    onChange(selectedCountry.dialCode + num);
  };

  return (
    <div className={`relative flex items-center gap-2 ${className}`}>
      {/* Country Selector */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setIsOpen(!isOpen)}
          className="flex h-16 items-center gap-2 rounded-2xl bg-slate-50/50 border border-slate-100 px-4 text-sm font-bold hover:bg-white focus:border-emerald-500 transition-all active:scale-[0.98] disabled:opacity-50"
        >
          <span className="text-xl">{selectedCountry.flag}</span>
          <span className="text-slate-600">{selectedCountry.dialCode}</span>
          <ChevronDown size={14} className={`text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </button>

        {isOpen && (
          <div className="absolute left-0 top-full z-50 mt-2 w-64 rounded-2xl bg-white border border-slate-100 shadow-2xl p-2 animate-in fade-in slide-in-from-top-2">
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
              <input 
                autoFocus
                placeholder="Search country..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full h-10 rounded-xl bg-slate-50 border-none pl-9 pr-4 text-xs font-bold text-slate-900 focus:ring-2 focus:ring-emerald-500 outline-none"
              />
            </div>
            <div className="max-h-60 overflow-y-auto custom-scrollbar">
              {filteredCountries.map(c => (
                <button
                  key={c.code + c.dialCode}
                  type="button"
                  onClick={() => handleCountrySelect(c)}
                  className="flex w-full items-center gap-3 rounded-xl p-3 text-left hover:bg-emerald-50 transition-colors group"
                >
                  <span className="text-xl">{c.flag}</span>
                  <div className="flex-1">
                    <p className="text-xs font-bold text-slate-900 group-hover:text-emerald-700">{c.name}</p>
                    <p className="text-[10px] text-slate-400">{c.dialCode}</p>
                  </div>
                  {selectedCountry.code === c.code && (
                    <div className="h-2 w-2 rounded-full bg-emerald-500" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Number Input */}
      <input
        type="tel"
        disabled={disabled}
        value={displayValue}
        onChange={handleNumberChange}
        placeholder={placeholder || "000 000 000"}
        className="flex-1 h-16 rounded-2xl bg-slate-50/50 border border-slate-100 px-6 text-sm font-bold text-slate-950 outline-none focus:bg-white focus:border-emerald-500 transition-all placeholder:text-slate-300 disabled:opacity-50"
      />
    </div>
  );
}
